import { z } from 'zod';
import { GENDERS } from './roles';

// ─────────────── Discovery (Phase 3) ───────────────

export const discoverQuerySchema = z.object({
  /** Kilometres around the viewer. 0 means any distance; left out means the admin's default. */
  radiusKm: z.coerce.number().min(0).max(500).optional(),
  gender: z.enum(GENDERS).optional(),
  minutes: z.coerce.number().int().positive().optional(),
  maxPrice: z.coerce.number().int().positive().optional(),
  minRating: z.coerce.number().min(0).max(5).optional(),
  availableOnly: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
  sort: z.enum(['nearest', 'recommended']).optional(),
  offset: z.coerce.number().int().min(0).max(5000).default(0),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type DiscoverQuery = z.infer<typeof discoverQuerySchema>;

export const REPORT_CATEGORIES = ['FAKE_PROFILE', 'INAPPROPRIATE', 'NO_SHOW', 'RUDE', 'SAFETY', 'SCAM', 'OTHER'] as const;
export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export const reportSchema = z.object({
  providerId: z.string().uuid(),
  category: z.enum(REPORT_CATEGORIES),
  details: z.string().trim().max(500).optional(),
  bookingId: z.string().uuid().optional(),
});

export const availabilitySchema = z.object({ available: z.boolean() });

/** Rounded distance shown to users. Exact positions are never sent. */
export function displayDistanceKm(km: number | null | undefined): number | null {
  if (km === null || km === undefined || !Number.isFinite(km)) return null;
  if (km < 1) return 0.5; // shown as "under 1 km"
  return Math.round(km * 10) / 10;
}

// ─────────────── Bookings (Phase 4) ───────────────

export const createBookingSchema = z.object({
  providerId: z.string().uuid(),
  minutes: z.number().int().positive(),
  /** Generated by the app per tap; repeating it never creates a second request. */
  idempotencyKey: z.string().min(8).max(64),
});

export const CANCEL_REASONS = ['PRICE', 'CHANGED_MIND', 'PROVIDER_CANCELED', 'OTHER'] as const;
export const cancelBookingSchema = z.object({
  reason: z.enum(CANCEL_REASONS),
  note: z.string().trim().max(300).optional(),
});

/**
 * Free request credits: `quota` per period, refilled when the period ends.
 * Pure function so it can be tested and used the same way everywhere.
 */
export function rechargeQuota(
  state: { freeCredits: number; nextRechargeAt: Date },
  now: Date,
  quota: number,
  intervalHours: number,
): { freeCredits: number; nextRechargeAt: Date; recharged: boolean } {
  if (now < state.nextRechargeAt) return { ...state, recharged: false };
  return {
    freeCredits: Math.max(state.freeCredits, quota),
    nextRechargeAt: new Date(now.getTime() + intervalHours * 3600_000),
    recharged: true,
  };
}

// ─────────────── Chat (Phase 5) ───────────────

export const MESSAGE_TYPES = ['TEXT', 'PHOTO', 'VOICE', 'VIDEO'] as const;
export type MessageType = (typeof MESSAGE_TYPES)[number];

export const sendMessageSchema = z
  .object({
    clientMsgId: z.string().min(8).max(64),
    type: z.enum(MESSAGE_TYPES),
    text: z.string().trim().max(2000).optional(),
    mediaKey: z.string().max(200).optional(),
    durationSec: z.number().min(0).max(600).optional(),
  })
  .refine((m) => (m.type === 'TEXT' ? !!m.text : !!m.mediaKey), { message: 'MESSAGE_EMPTY' });
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const CHAT_UPLOAD_PURPOSES = ['chat_photo', 'chat_voice', 'chat_video'] as const;
/** Admin-only: a photo, video or voice message sent with a broadcast. */
export const BROADCAST_UPLOAD_PURPOSES = ['broadcast_photo', 'broadcast_voice', 'broadcast_video'] as const;
